import React, { useState, useRef, useEffect } from "react";
import Barcode from "react-barcode";
import CureBayLogoBlack from "../Assets/CureBayLogoBlack.svg";
import apiService from "../Services/authenticationServices";

const LABEL_CSS = `
  .shipment-label {
    width: 360px;
    border: 2px solid #000;
    border-radius: 14px;
    padding: 24px 20px;
    font-family: "Arial", sans-serif;
    color: #000;
    background: #fff;
  }
  .label-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
  .label-sender {
    font-size: 12px;
    line-height: 1.5;
  }
  .label-sender span:first-of-type {
    text-transform: uppercase;
    font-weight: 600;
    display: block;
    margin-bottom: 2px;
  }
  .label-sender strong {
    display: block;
    font-size: 13px;
    margin-bottom: 2px;
  }
  .label-logo {
    width: 80px;
    height: auto;
    object-fit: contain;
  }
  .label-divider {
    border-top: 1.5px solid #000;
    margin: 18px 0;
  }
  .label-recipient-name {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 8px;
  }
  .label-recipient-address {
    font-size: 14px;
    line-height: 1.45;
  }
  .label-stats {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    gap: 12px;
  }
  .label-stats strong {
    display: block;
    font-size: 15px;
    margin-top: 6px;
    text-transform: none;
  }
  .label-barcode {
    margin: 22px 0;
    text-align: center;
  }
  .label-barcode svg,
  .label-barcode canvas {
    width: 100%;
    height: auto;
  }
  .label-footer {
    border-top: 1.5px solid #000;
    padding-top: 10px;
    font-size: 12px;
    text-align: center;
  }
`;

export default function ShipmentBarcode({ isOpen, consignmentId, onClose }) {
  const printRef = useRef();
  const [shipmentData, setShipmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [barcodeValue, setBarcodeValue] = useState("");
  const remarks = shipmentData?.remarks ?? "Please leave with reception";

  const getAddressLines = (data) => {
    if (!data) return [];
    const lines = [
      data.deliveryAddress1,
      data.deliveryAddress2,
      data.deliveryAddress3,
    ].filter(Boolean);
    const cityStateZip = [data.deliveryCity, data.deliveryState, data.deliveryZipCode]
      .filter(Boolean)
      .join(", ");
    if (cityStateZip) lines.push(cityStateZip);
    if (data.deliveryCountry) lines.push(data.deliveryCountry);
    return lines;
  };

  useEffect(() => {
    if (!isOpen || !consignmentId) {
      setShipmentData(null);
      setBarcodeValue("");
      return;
    }

    const fetchShipmentDetails = async () => {
      setIsLoading(true);
      setShipmentData(null);

      try {
        console.log("Fetching shipment details for consignmentId:", consignmentId);

        const response = await apiService.getShipmentDetails(consignmentId);
        console.log("API response:", response);

        const orderDetail = response?.data;

        if (!orderDetail || !orderDetail.consignmentId) {
          throw new Error("No matching shipment found in the response.");
        }

        const isCheOrder = !!orderDetail?.cheName;

        const shipment = {
          consignmentId: orderDetail.consignmentId,
          clinicName: isCheOrder ? orderDetail.cheName || "" : orderDetail.clinicName || "",
          recipientName: isCheOrder
            ? orderDetail.deliveryContactName ||
            orderDetail.recipientName ||
            orderDetail.receiverName ||
            orderDetail.patientName ||
            orderDetail.cheName ||
            ""
            : orderDetail.recipientName ||
            orderDetail.receiverName ||
            orderDetail.patientName ||
            orderDetail.clinicName ||
            "",
          deliveryContactName: orderDetail.deliveryContactName || null,
          deliveryAddress1: orderDetail.deliveryAddress1 || "",
          deliveryAddress2: orderDetail.deliveryAddress2 || "",
          deliveryAddress3: orderDetail.deliveryAddress3 || "",
          deliveryCity: orderDetail.deliveryCity || "",
          deliveryState: orderDetail.deliveryState || "",
          deliveryZipCode:
            orderDetail.deliveryZipCode || orderDetail.deliveryPinCode || "",
          deliveryCountry: orderDetail.deliveryCountry || "India",
          reference:
            orderDetail.poNumber ||
            orderDetail.referenceNumber ||
            orderDetail.orderNumber ||
            orderDetail.reference ||
            "-",
          remarks: orderDetail.remarks || "Please leave with reception",
          isCheOrder,
        };

        let barcode = orderDetail.consignmentId;

        console.log("Final Barcode for this order:", barcode);

        setShipmentData(shipment);
        setBarcodeValue(barcode);

      } catch (error) {
        alert(`Failed to fetch shipment details: ${error.message}`);
        setShipmentData(null);
        setBarcodeValue("");
      } finally {
        setIsLoading(false);
      }
    };

    fetchShipmentDetails();
  }, [isOpen, consignmentId]);

  if (!isOpen) return null;

  const handlePrint = () => {
    if (!printRef.current) return;
    const printContents = printRef.current.innerHTML;
    const printWindow = window.open("", "", "width=800,height=600");
    printWindow.document.write(`
      <html>
        <head>
          <title>Shipment Label</title>
          <style>${LABEL_CSS}</style>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              background: white;
              margin: 0;
            }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2 sm:p-0">
      <div className="bg-white rounded-cb-md shadow-2xl w-full max-w-[420px] max-h-[90dvh] overflow-auto p-4 sm:p-6 relative border border-gray-400">
        <div ref={printRef}>
          {isLoading && <div className="text-center py-8">Loading...</div>}
          {!isLoading && !shipmentData && (
            <div className="text-center py-8 text-red-600">
              No shipment details found.
            </div>
          )}
          {!isLoading && shipmentData && (
            <div className="w-full flex justify-start sm:justify-center overflow-x-auto">
              <style>{LABEL_CSS}</style>
              <div className="shipment-label">
                <div className="label-header">
                  <img
                    src={CureBayLogoBlack}
                    alt="Logo"
                    className="label-logo ml-[75%] "
                  />
                </div>

                <div>
                  <div className="label-recipient-name">
                    <span>Ship To:</span>
                  </div>

                  {shipmentData.deliveryContactName && (
                    <div className="label-recipient-address">
                      {shipmentData.deliveryContactName}
                    </div>
                  )}
                  <div className="label-recipient-address">
                    {shipmentData.isCheOrder
                      ? shipmentData.recipientName || ""
                      : shipmentData.recipientName || shipmentData.clinicName || ""}
                  </div>

                  <div className="label-recipient-address">
                    {getAddressLines(shipmentData).map((line, index) => (
                      <div key={index}>{line}</div>
                    ))}
                  </div>
                </div>

                <div className="label-divider" />

                <div className="label-stats">
                  <div>
                    <span>Tracking ID:</span>
                    <strong>{shipmentData.consignmentId || "-"}</strong>
                  </div>
                  <div>
                    <span>Remarks:</span>
                    <strong></strong>
                  </div>
                </div>

                <div className="label-divider" />

                <div className="label-barcode">
                  {barcodeValue && (
                    <Barcode
                      value={barcodeValue}
                      format="CODE128"
                      width={2}
                      height={70}
                      displayValue={false}
                    />
                  )}
                </div>

                <div className="label-footer">
                  Delivery Instruction: {remarks}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-center gap-4">
          <button
            onClick={handlePrint}
            disabled={!shipmentData}
            className="bg-[#142143] text-white px-4 py-2 rounded-lg hover:bg-[#142143] disabled:opacity-50"
          >
            Print
          </button>
          <button
            onClick={onClose}
            className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

